import axios from 'axios';

// Fallback points at PRODUCTION (not the relative '/api', which breaks in the
// native app). VITE_API_URL still overrides for local dev / other envs.
const DEFAULT_API_URL = 'https://gleaming-elegance-production-d593.up.railway.app/api';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || DEFAULT_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('manager_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A stored token that has already expired must land on the login screen
// once, cleanly — not render the app from cache and then bounce the user on
// their first real request.
(() => {
  const token = localStorage.getItem('manager_token');
  if (!token) return;
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    if (exp && exp * 1000 < Date.now()) {
      localStorage.removeItem('manager_token');
      localStorage.removeItem('manager_refresh_token');
      localStorage.removeItem('manager_user');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?session=expired';
      }
    }
  } catch {
    // unreadable token — leave it; the 401 handler below cleans up
  }
})();

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('manager_token');
      localStorage.removeItem('manager_refresh_token');
      localStorage.removeItem('manager_user');
      window.location.href = '/login?session=expired';
    }
    return Promise.reject(error);
  }
);

// Surface the server's read-only-grace signal (x-trial-grace on GETs after
// trial expiry). A successful write means the subscription is healthy again.
api.interceptors.response.use(
  (response) => {
    const g = response.headers?.['x-trial-grace'];
    if (g) {
      if (localStorage.getItem('mise_trial_grace') !== String(g)) {
        localStorage.setItem('mise_trial_grace', String(g));
        window.dispatchEvent(new Event('mise-grace'));
      }
    } else if ((response.config?.method || 'get') !== 'get' && localStorage.getItem('mise_trial_grace')) {
      localStorage.removeItem('mise_trial_grace');
      window.dispatchEvent(new Event('mise-grace'));
    }
    return response;
  },
  (error) => {
    // Subscription lapsed: route to billing instead of surfacing raw errors
    // on every action. Native builds show the web-subscription notice page
    // (App Store 3.1.1 keeps checkout on the web).
    if (error.response?.status === 402 && !window.location.pathname.startsWith('/billing')) {
      window.location.href = '/billing';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/v1/auth/login', { email, password }),

  register: (data: RegisterData) =>
    api.post('/v1/auth/register', data),

  me: () =>
    api.get('/v1/auth/me'),

  logout: () =>
    api.post('/v1/auth/logout'),

  refreshToken: (refreshToken: string) =>
    api.post('/v1/auth/refresh', { refreshToken }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/v1/auth/change-password', { currentPassword, newPassword }),

  switchRestaurant: (restaurantId: string) =>
    api.post('/v1/auth/switch-restaurant', { restaurantId }),

  forgotPassword: (email: string) =>
    api.post('/v1/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post('/v1/auth/reset-password', { token, password }),

  verifyEmail: (token: string) =>
    api.post('/v1/auth/verify-email', { token }),

  resendVerification: () =>
    api.post('/v1/auth/resend-verification'),

  updateProfile: (data: { firstName?: string; lastName?: string; email?: string }) =>
    api.patch('/v1/auth/profile', data),
};

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  restaurantName: string;
  restaurantType: string;
  acquisition?: { referrer?: string; landing?: string; utmSource?: string; utmMedium?: string; utmCampaign?: string };
  website_url?: string;
  menuImportToken?: string;
}

// Onboarding endpoints
export const onboardingApi = {
  getStatus: () =>
    api.get('/v1/onboarding/status'),

  updateRestaurant: (data: UpdateRestaurantData) =>
    api.patch('/v1/onboarding/restaurant', data),

  quickMenuSetup: (data: { categories: MenuCategorySetup[] }) =>
    api.post('/v1/onboarding/menu/quick-setup', data),

  batchCreateStaff: (data: { employees: EmployeeSetup[] }) =>
    api.post('/v1/onboarding/staff/batch', data),

  // Floor plan setup
  setupFloorPlan: (data: { sections: FloorPlanSection[] }) =>
    api.post('/v1/onboarding/floor-plan', data),

  getFloorPlan: () =>
    api.get('/v1/onboarding/floor-plan'),

  assignServerToSection: (sectionId: string, employeeId: string) =>
    api.post(`/v1/onboarding/sections/${sectionId}/assign`, { employeeId }),

  complete: () =>
    api.post('/v1/onboarding/complete'),

  skipPayment: () =>
    api.post('/v1/onboarding/skip-payment'),

  requestHelp: (data: { currentStep: string; message?: string }) =>
    api.post('/v1/onboarding/request-help', data),

  importFromWebsite: (url: string) =>
    api.post('/v1/onboarding/import-from-website', { url }),
};

interface FloorPlanSection {
  name: string;
  color?: string;
  tables?: FloorPlanTable[];
}

interface FloorPlanTable {
  number: string;
  name?: string;
  capacity: number;
  shape?: 'round' | 'square' | 'rectangle';
  posX?: number;
  posY?: number;
}

interface UpdateRestaurantData {
  name?: string;
  phone?: string;
  email?: string;
  timezone?: string;
  addressStreet1?: string;
  addressStreet2?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  addressCountry?: string;
}

interface MenuCategorySetup {
  name: string;
  items: {
    name: string;
    description?: string;
    priceCents: number;
  }[];
}

interface EmployeeSetup {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  role: string;
  pin: string;
  hourlyRateCents?: number;
}

// Analytics endpoints
export const analyticsApi = {
  getDashboard: () => api.get('/v1/analytics/dashboard'),
  getSalesReport: (params: { startDate: string; endDate: string }) =>
    api.get('/v1/analytics/sales', { params }),
  getMenuPerformance: (params?: { days?: number; startDate?: string; endDate?: string; limit?: number }) =>
    api.get('/v1/analytics/menu-performance', { params }),
  getLaborReport: (params: { startDate: string; endDate: string }) =>
    api.get('/v1/analytics/labor', { params }),
  getKitchenPerformance: (params: { startDate: string; endDate: string }) =>
    api.get('/v1/analytics/kitchen-performance', { params }),
  getServerPerformance: (params: { startDate: string; endDate: string }) =>
    api.get('/v1/analytics/server-performance', { params }),
  getHourlySales: (date: string) =>
    api.get('/v1/analytics/hourly-sales', { params: { date } }),
};

// Staff endpoints
export const staffApi = {
  getAll: (params?: { includeTerminated?: boolean }) =>
    api.get('/v1/staff', { params: params?.includeTerminated ? { includeTerminated: true } : {} }),
  getById: (id: string) => api.get(`/v1/staff/${id}`),
  create: (data: CreateStaffData) => api.post('/v1/staff', data),
  update: (id: string, data: UpdateStaffData) =>
    api.patch(`/v1/staff/${id}`, data),
  delete: (id: string) => api.delete(`/v1/staff/${id}`),
  // Get ALL active employees (for scheduling, regardless of clock-in status)
  getAvailable: (role?: string) =>
    api.get('/v1/staff/available', { params: role ? { role } : {} }),
  // Schedule management
  getSchedule: (params: { startDate: string; endDate: string }) =>
    api.get('/v1/staff/schedule', { params }),
  createShift: (data: CreateShiftData) =>
    api.post('/v1/staff/schedule/shifts', data),
  updateShift: (shiftId: string, data: UpdateShiftData) =>
    api.patch(`/v1/staff/schedule/shifts/${shiftId}`, data),
  deleteShift: (shiftId: string) =>
    api.delete(`/v1/staff/schedule/shifts/${shiftId}`),
  confirmShift: (shiftId: string) =>
    api.post(`/v1/staff/schedule/shifts/${shiftId}/confirm`),
  generateShifts: (data: GenerateShiftsData) =>
    api.post('/v1/staff/schedule/generate', data),
  getClockedIn: () => api.get('/v1/staff', { params: { clockedIn: true } }),
  getTimeEntries: (employeeId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get(`/v1/staff/${employeeId}/time-entries`, { params }),
  // Shift history for all employees (completed shifts with reports)
  getShiftHistory: (params?: { startDate?: string; endDate?: string; employeeId?: string }) =>
    api.get('/v1/staff/shift-history', { params }),
  // Active shifts management (manager controls)
  getActiveShifts: () => api.get('/v1/staff/shifts/active'),
  getShiftOverview: () => api.get('/v1/staff/shifts/overview'),
  getSections: () => api.get('/v1/floor-plan/sections'),
  managerClockOut: (employeeId: string) =>
    api.post(`/v1/staff/${employeeId}/manager/clock-out`),
  managerClockOutAll: () =>
    api.post('/v1/staff/manager/clock-out-all'),
  managerStartBreak: (employeeId: string) =>
    api.post(`/v1/staff/${employeeId}/manager/break/start`),
  managerEndBreak: (employeeId: string) =>
    api.post(`/v1/staff/${employeeId}/manager/break/end`),
  addShiftNote: (employeeId: string, note: string) =>
    api.patch(`/v1/staff/${employeeId}/shift-note`, { note }),
  reassignSection: (employeeId: string, sectionId: string) =>
    api.patch(`/v1/staff/${employeeId}/reassign-section`, { sectionId }),
  managerClockIn: (employeeId: string, sectionId?: string, stationId?: string) =>
    api.post(`/v1/staff/${employeeId}/manager/clock-in`, { sectionId, stationId }),
  // Scheduled but not clocked in
  getScheduledNotClockedIn: () => api.get('/v1/staff/scheduled/not-clocked-in'),
  // Role permissions
  getRolePermissions: () =>
    api.get('/v1/staff/settings/role-permissions'),
  updateRolePermissions: (role: string, data: RolePermissionsData) =>
    api.put(`/v1/staff/settings/role-permissions/${role}`, data),
  // Resend onboarding email
  resendOnboarding: (employeeId: string) =>
    api.post(`/v1/staff/${employeeId}/resend-onboarding`),
};

interface CreateShiftData {
  employeeId: string;
  scheduledStart: string;
  scheduledEnd: string;
  role: string;
  station?: string;
  section?: string;
  notes?: string;
}

interface UpdateShiftData {
  scheduledStart?: string;
  scheduledEnd?: string;
  role?: string;
  station?: string;
  section?: string;
  status?: string;
  notes?: string;
}

interface GenerateShiftsData {
  date: string;
  staffToSchedule?: Array<{
    employeeId: string;
    shiftType: 'am' | 'pm' | 'full';
  }>;
}

// Kitchen Stations endpoints
export const kitchenStationsApi = {
  getAll: () => api.get('/v1/kitchen-stations'),
  create: (data: CreateKitchenStationData) => api.post('/v1/kitchen-stations', data),
  update: (id: string, data: Partial<CreateKitchenStationData>) =>
    api.patch(`/v1/kitchen-stations/${id}`, data),
  delete: (id: string) => api.delete(`/v1/kitchen-stations/${id}`),
  assign: (stationId: string, employeeId: string, notes?: string) =>
    api.post(`/v1/kitchen-stations/${stationId}/assign`, { employeeId, notes }),
  unassign: (stationId: string) =>
    api.post(`/v1/kitchen-stations/${stationId}/unassign`),
  getAvailableCooks: () => api.get('/v1/kitchen-stations/available-cooks'),
};

interface CreateKitchenStationData {
  name: string;
  shortName?: string;
  displayOrder?: number;
  color?: string;
}

interface RolePermissionsData {
  canClockInWithoutApproval?: boolean;
  canAccessPos?: boolean;
  requiresSectionAssignment?: boolean;
  maxShiftHours?: number;
  breakReminderMinutes?: number;
  requiresPreShiftChecklist?: boolean;
}

interface CreateStaffData {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  primaryRole: string;
  pin: string;
  hourlyRateCents?: number;
}

interface UpdateStaffData extends Partial<CreateStaffData> {
  status?: 'active' | 'inactive';
}

// Menu endpoints
export const menuApi = {
  importMenu: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/v1/menu/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getCategories: () => api.get('/v1/menu/categories'),
  createCategory: (data: { name: string; description?: string }) =>
    api.post('/v1/menu/categories', data),
  getModifierGroups: () => api.get('/v1/menu/modifier-groups'),
  createModifierGroup: (data: any) => api.post('/v1/menu/modifier-groups', data),
  updateModifierGroup: (groupId: string, data: any) =>
    api.patch(`/v1/menu/modifier-groups/${groupId}`, data),
  deleteModifierGroup: (groupId: string) =>
    api.delete(`/v1/menu/modifier-groups/${groupId}`),
  setItemModifierGroups: (itemId: string, groupIds: string[]) =>
    api.put(`/v1/menu/items/${itemId}/modifier-groups`, { groupIds }),
  getItems: (categoryId?: string) =>
    api.get('/v1/menu/items', { params: { categoryId } }),
  getItem: (id: string) => api.get(`/v1/menu/items/${id}`),
  createItem: (data: CreateMenuItemData) => api.post('/v1/menu/items', data),
  updateItem: (id: string, data: Partial<CreateMenuItemData>) =>
    api.patch(`/v1/menu/items/${id}`, data),
  deleteItem: (id: string) => api.delete(`/v1/menu/items/${id}`),
  updateAvailability: (id: string, isAvailable: boolean) =>
    api.patch(`/v1/menu/items/${id}/availability`, { isAvailable }),
};

interface CreateMenuItemData {
  name: string;
  shortName?: string;
  description?: string;
  priceCents: number;
  categoryId?: string;
  isAvailable?: boolean;
  prepTimeMinutes?: number;
  stations?: string[];
}

// Tables endpoints
export const tablesApi = {
  getAll: () => api.get('/v1/tables'),
  getStats: () => api.get('/v1/tables/stats'),
  updateStatus: (id: string, status: string) =>
    api.patch(`/v1/tables/${id}/status`, { status }),
  update: (id: string, data: UpdateTableData) =>
    api.patch(`/v1/tables/${id}`, data),
  delete: (id: string) => api.delete(`/v1/tables/${id}`),
  clear: (id: string) => api.post(`/v1/tables/${id}/clear`),
  move: (fromTableId: string, toTableId: string) =>
    api.post('/v1/tables/move', { fromTableId, toTableId }),
};

interface UpdateTableData {
  number?: string;
  name?: string;
  capacity?: number;
  shape?: string;
  positionX?: number;
  positionY?: number;
  sectionId?: string;
}

// Sections endpoints
export const sectionsApi = {
  getAll: () => api.get('/v1/sections'),
  create: (data: CreateSectionData) => api.post('/v1/sections', data),
  update: (id: string, data: Partial<CreateSectionData>) =>
    api.patch(`/v1/sections/${id}`, data),
  delete: (id: string) => api.delete(`/v1/sections/${id}`),
  addTable: (sectionId: string, data: CreateTableData) =>
    api.post(`/v1/sections/${sectionId}/tables`, data),
  initialize: (options?: { tableCount?: number; includePatio?: boolean; includeBar?: boolean }) =>
    api.post('/v1/sections/initialize', options || {}),
  updateTablePosition: (tableId: string, data: { positionX: number; positionY: number; sectionId?: string }) =>
    api.patch(`/v1/sections/tables/${tableId}/position`, data),
};

interface CreateSectionData {
  name: string;
  color?: string;
}

interface CreateTableData {
  number: string;
  name?: string;
  capacity: number;
  shape?: 'round' | 'square' | 'rectangle';
  positionX?: number;
  positionY?: number;
}

// Reservations endpoints
export const reservationsApi = {
  getAll: (params?: { date?: string; status?: string }) =>
    api.get('/v1/reservations', { params }),
  create: (data: CreateReservationData) => api.post('/v1/reservations', data),
  update: (id: string, data: Partial<CreateReservationData>) =>
    api.patch(`/v1/reservations/${id}`, data),
  cancel: (id: string, reason?: string) =>
    api.post(`/v1/reservations/${id}/cancel`, { reason }),
  confirm: (id: string) => api.post(`/v1/reservations/${id}/confirm`),
  seat: (id: string, tableId: string) =>
    api.post(`/v1/reservations/${id}/seat`, { tableId }),
};

interface CreateReservationData {
  guestName: string;
  guestPhone?: string;
  guestEmail?: string;
  partySize: number;
  date: string;
  time: string;
  notes?: string;
}

// Inventory endpoints
export const inventoryApi = {
  getItems: () => api.get('/v1/inventory'),
  getItem: (id: string) => api.get(`/v1/inventory/${id}`),
  createItem: (data: CreateInventoryItemData) => api.post('/v1/inventory', data),
  updateItem: (id: string, data: Partial<CreateInventoryItemData>) =>
    api.patch(`/v1/inventory/${id}`, data),
  adjustQuantity: (id: string, quantity: number, reason: string) =>
    api.post(`/v1/inventory/${id}/transactions`, {
      type: 'adjustment',
      quantity,
      notes: reason
    }),
  receiveFromInvoice: (data: { lines: any[]; vendorName?: string; invoiceNumber?: string }) =>
    api.post('/v1/inventory/receive-from-invoice', data),
  getLowStock: () => api.get('/v1/inventory/low-stock'),
  getAlerts: () => api.get('/v1/inventory/alerts'),
  getSuggestions: () => api.get('/v1/inventory/suggestions'),

  // Ordering intelligence
  getOrderSuggestions: (params?: { urgentOnly?: boolean; vendorId?: string }) =>
    api.get('/v1/inventory/order-suggestions', { params }),
  createOrdersFromSuggestions: (lines: OrderSuggestionLine[]) =>
    api.post('/v1/inventory/order-suggestions/create-orders', { lines }),
  getItemSuppliers: (itemId: string) =>
    api.get(`/v1/inventory/${itemId}/suppliers`),

  // Vendors
  getVendors: () => api.get('/v1/inventory/vendors'),
  createVendor: (data: CreateVendorData) => api.post('/v1/inventory/vendors', data),

  // Vendor catalog (per-supplier pricing)
  getVendorCatalog: (vendorId: string) =>
    api.get(`/v1/inventory/vendors/${vendorId}/catalog`),
  addCatalogItem: (vendorId: string, data: CatalogItemData) =>
    api.post(`/v1/inventory/vendors/${vendorId}/catalog`, data),
  updateCatalogItem: (catalogId: string, data: Partial<CatalogItemData>) =>
    api.patch(`/v1/inventory/catalog/${catalogId}`, data),
  deleteCatalogItem: (catalogId: string) =>
    api.delete(`/v1/inventory/catalog/${catalogId}`),

  // Purchase orders
  getPurchaseOrders: (params?: { status?: string; vendorId?: string }) =>
    api.get('/v1/inventory/purchase-orders', { params }),
  createPurchaseOrder: (data: CreatePurchaseOrderData) =>
    api.post('/v1/inventory/purchase-orders', data),
  receivePurchaseOrder: (orderId: string, data: ReceivePurchaseOrderData) =>
    api.post(`/v1/inventory/purchase-orders/${orderId}/receive`, data),
  sendPurchaseOrder: (orderId: string, vendorEmail?: string) =>
    api.post(`/v1/inventory/purchase-orders/${orderId}/send`, vendorEmail ? { vendorEmail } : {}),
  getCostHistory: (itemId: string) =>
    api.get(`/v1/inventory/${itemId}/cost-history`),

  // Recurring supplier orders
  getRecurringOrders: () => api.get('/v1/inventory/recurring-orders'),
  createRecurringOrder: (data: {
    vendorId: string;
    items: { itemId: string; quantity: number; unitCost: number }[];
    frequencyDays: number;
    nextDate?: string;
    autoSend?: boolean;
  }) => api.post('/v1/inventory/recurring-orders', data),
  updateRecurringOrder: (id: string, data: { active?: boolean; autoSend?: boolean; frequencyDays?: number; nextDate?: string }) =>
    api.patch(`/v1/inventory/recurring-orders/${id}`, data),
  deleteRecurringOrder: (id: string) => api.delete(`/v1/inventory/recurring-orders/${id}`),

  // Duplicate records (e.g. R365 double-imports): report first, then a
  // human-confirmed merge per pair — history moves to the survivor.
  getDuplicates: () => api.get('/v1/inventory/duplicates'),
  mergeItems: (survivorId: string, mergedId: string) =>
    api.post('/v1/inventory/merge', { survivorId, mergedId }),
};

interface OrderSuggestionLine {
  itemId: string;
  quantity: number;
  vendorId?: string;
  unitCost?: number;
}

interface CreateVendorData {
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  paymentTerms?: string;
}

interface CatalogItemData {
  inventoryItemId?: string;
  vendorSku?: string;
  vendorItemName: string;
  unitPriceCents: number;
  orderUnit: string;
  packSize?: number;
  packUnit?: string;
  minimumOrder?: number;
  leadTimeDays?: number;
  isPreferred?: boolean;
}

interface CreatePurchaseOrderData {
  vendorId: string;
  items: { itemId: string; quantity: number; unitCost?: number }[];
  expectedDelivery?: string;
  notes?: string;
}

interface ReceivePurchaseOrderData {
  items?: {
    poItemId?: string;
    itemId?: string;
    receivedQuantity: number;
    damagedQuantity?: number;
    note?: string;
  }[];
  deliveryNotes?: string;
}

// Invoices & reconciliation endpoints
export const invoicesApi = {
  getAll: (params?: { status?: string; matchStatus?: string; vendorId?: string }) =>
    api.get('/v1/invoices', { params }),
  getById: (id: string) => api.get(`/v1/invoices/${id}`),
  getDiscrepancies: () => api.get('/v1/invoices/discrepancies'),
  create: (data: CreateInvoiceData) => api.post('/v1/invoices', data),
  match: (id: string, purchaseOrderId?: string) =>
    api.post(`/v1/invoices/${id}/match`, purchaseOrderId ? { purchaseOrderId } : {}),
  approve: (id: string) => api.post(`/v1/invoices/${id}/approve`),
  dispute: (id: string, reason?: string) =>
    api.post(`/v1/invoices/${id}/dispute`, reason ? { reason } : {}),
  delete: (id: string) => api.delete(`/v1/invoices/${id}`),
  attachImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/v1/invoices/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Upload a photo of a paper invoice; returns { imageUrl, parsed } where
  // parsed prefills the form (number, dates, totals, line items).
  parse: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/v1/invoices/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

interface CreateInvoiceData {
  vendorId: string;
  imageUrl?: string;
  purchaseOrderId?: string;
  invoiceNumber: string;
  invoiceDate?: string;
  dueDate?: string;
  taxCents?: number;
  notes?: string;
  lineItems?: {
    inventoryItemId?: string;
    description: string;
    quantity: number;
    unit?: string;
    unitPriceCents: number;
  }[];
}

// Reports endpoints
export const reportsApi = {
  // restaurantId: a sibling store on the same account (store comparison)
  getFoodCost: (params?: { period?: 'today' | 'week' | 'month'; startDate?: string; endDate?: string; restaurantId?: string }) =>
    api.get('/v1/reports/food-cost', { params }),
};

// P&L endpoints (per-store profit & loss + all-stores roll-up)
export const pnlApi = {
  // restaurantId: a store id, or 'all' for the roll-up
  getStatement: (params: {
    restaurantId?: string;
    period?: 'thisWeek' | 'thisMonth' | 'lastMonth';
    start?: string;
    end?: string;
    periodKey?: string;
  }) => api.get('/v1/pnl', { params }),
  // Editable config the UI hydrates from (categories + vendor map + this store's manual lines)
  getConfig: () => api.get('/v1/pnl/config'),
  // Vendors (deduped by name) with current/auto category — for the "Categorize spend" editor
  getVendors: () => api.get('/v1/pnl/vendors'),
  // Replace the account category list (shared across stores)
  putCategories: (categories: Array<{ key: string; label: string; group: 'cogs' | 'opex'; order?: number }>) =>
    api.put('/v1/pnl/categories', { categories }),
  // Map a vendor (by name) to a category or 'excluded' (shared)
  setVendorCategory: (vendorName: string, category: string) =>
    api.put('/v1/pnl/vendor-category', { vendorName, category }),
  // Manual lines (per store)
  addManualLine: (data: { category: string; label: string; amountCents: number; periodKey?: string }) =>
    api.post('/v1/pnl/manual-line', data),
  updateManualLine: (id: string, data: { category?: string; label?: string; amountCents?: number; periodKey?: string }) =>
    api.put(`/v1/pnl/manual-line/${id}`, data),
  deleteManualLine: (id: string) => api.delete(`/v1/pnl/manual-line/${id}`),
};

interface CreateInventoryItemData {
  name: string;
  sku?: string;
  unit: string;
  category?: string;
  currentQuantity?: number;
  parLevel?: number;
  unitCost?: number;
  supplier?: string;
  supplierContact?: string;
}

// Orders endpoints
export const ordersApi = {
  getRecent: (limit?: number) => api.get('/v1/orders', { params: { limit } }),
  getById: (id: string) => api.get(`/v1/orders/${id}`),
};

// Recipes endpoints
export const productionApi = {
  getMappings: () => api.get('/v1/production/mappings'),
  createMapping: (data: { sourceName: string; outputs: Array<{ className: string; flavor?: string; unitsPerQty: number }> }) =>
    api.post('/v1/production/mappings', data),
  updateMapping: (id: string, data: Record<string, unknown>) => api.patch(`/v1/production/mappings/${id}`, data),
  deleteMapping: (id: string) => api.delete(`/v1/production/mappings/${id}`),
  applySuggestions: () => api.post('/v1/production/mappings/apply-suggestions'),
  getOrders: () => api.get('/v1/production/orders'),
  addOrders: (data: unknown) => api.post('/v1/production/orders', data),
  updateOrder: (id: string, data: Record<string, unknown>) => api.patch(`/v1/production/orders/${id}`, data),
  deleteOrder: (id: string) => api.delete(`/v1/production/orders/${id}`),
  getDeliveries: (date?: string) => api.get('/v1/production/deliveries', { params: date ? { date } : {} }),
  setDeliveryStatus: (id: string, status: 'pending' | 'out' | 'delivered') => api.patch(`/v1/production/orders/${id}/delivery`, { status }),
  createItinerary: (data: { destination: string; startAddress: string; email: string; date?: string }) => api.post('/v1/production/deliveries/itinerary', data),
  emailPlanBreakdown: (data: { date?: string; email: string }) => api.post('/v1/production/plan/email', data),
  setBuffer: (bufferPerItem: number) => api.put('/v1/production/buffer', { bufferPerItem }),
  getPlan: (filters?: { date?: string; start?: string; end?: string; destination?: string; source?: string }) => {
    const params: Record<string, string> = {};
    if (filters?.date) params.date = filters.date;
    if (filters?.start && filters?.end) { params.start = filters.start; params.end = filters.end; }
    if (filters?.destination) params.destination = filters.destination;
    if (filters?.source) params.source = filters.source;
    return api.get('/v1/production/plan', { params });
  },
  getShopifyStatus: () => api.get('/v1/production/shopify/status'),
  getShopifyCatalog: (country?: string) => api.get('/v1/production/shopify/catalog', { params: country ? { country } : undefined }),
  getShopifyPricingCountries: () => api.get('/v1/production/shopify/pricing-countries'),
  getShopifyFulfillment: () => api.get('/v1/production/shopify/fulfillment'),
  getShopifySalesSummary: (days?: 7 | 30) => api.get('/v1/production/shopify/sales-summary', { params: days ? { days } : {} }),
  getHolidayBoard: (start?: string, end?: string) => api.get('/v1/production/holiday-board', { params: { start, end } }),
  startShopifyOauth: (shop: string) => api.get('/v1/production/shopify/oauth/start', { params: { shop } }),
  connectShopify: (data: { shopDomain: string; accessToken: string }) => api.post('/v1/production/shopify/connect', data),
  connectShopifyApp: (data: { shopDomain: string; clientId: string; clientSecret: string }) => api.post('/v1/production/shopify/connect-app', data),
  disconnectShopify: () => api.delete('/v1/production/shopify/connect'),
  syncShopify: () => api.post('/v1/production/shopify/sync'),
};

export const wholesaleApi = {
  getCustomers: () => api.get('/v1/wholesale/customers'),
  createCustomer: (data: { name: string; contactName?: string; email?: string; phone?: string; paymentTerms?: string; discountPercent?: number; notes?: string; category?: string }) =>
    api.post('/v1/wholesale/customers', data),
  updateCustomer: (id: string, data: Record<string, unknown>) => api.patch(`/v1/wholesale/customers/${id}`, data),
  getOrders: (params?: { status?: string; customerId?: string; entity?: string }) => api.get('/v1/wholesale/orders', { params }),
  createOrder: (data: { customerId: string; fulfillmentDate?: string; notes?: string; poNumber?: string; deliveryCostCents?: number; items: Array<{ menuItemId: string; quantity: number; unitPriceCents?: number }> }) =>
    api.post('/v1/wholesale/orders', data),
  // CPG summaries — revenue + margin by sales category, and per-drop delivery economics
  // entity: 'us' | 'eu' narrows to one set of books; omit for Combined
  getCategorySummary: (entity?: string) => api.get('/v1/wholesale/summary/categories', { params: entity ? { entity } : {} }),
  getDeliverySummary: (entity?: string) => api.get('/v1/wholesale/summary/delivery', { params: entity ? { entity } : {} }),
  // Standing weekly orders — the cron drafts each week's occurrence, you confirm
  setStandingWeekly: (customerId: string, data: { weekday: number; items: Array<{ menuItemId: string; quantity: number }> }) =>
    api.put(`/v1/wholesale/customers/${customerId}/standing-weekly`, data),
  clearStandingWeekly: (customerId: string) => api.delete(`/v1/wholesale/customers/${customerId}/standing-weekly`),
  makeStandingOrder: (orderId: string, weekday?: number) =>
    api.post(`/v1/wholesale/orders/${orderId}/make-standing`, weekday != null ? { weekday } : {}),
  // Scan/upload a distributor PO photo → { parsed: { poNumber, distributorName, orderDate, lineItems[] } }
  parseOrder: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/v1/wholesale/orders/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  setOrderStatus: (id: string, status: string) => api.patch(`/v1/wholesale/orders/${id}/status`, { status }),
  deleteOrder: (id: string) => api.delete(`/v1/wholesale/orders/${id}`),
  updateOrder: (id: string, data: Record<string, unknown>) => api.patch(`/v1/wholesale/orders/${id}`, data),
  setStage: (id: string, data: Record<string, unknown>) => api.patch(`/v1/wholesale/orders/${id}`, data),
  importOrders: (rows: Array<{ customerName: string; date?: string; notes?: string; items: Array<{ productName?: string; sku?: string; quantity: number; unitPriceCents?: number }> }>) => api.post('/v1/wholesale/orders/import', { rows }),
  fulfillOrder: (id: string, items: Array<{ orderItemId: string; quantity: number }>) => api.post(`/v1/wholesale/orders/${id}/fulfill`, { items }),
  getProduction: (range?: string | { start: string; end: string }) =>
    api.get('/v1/wholesale/production', { params: typeof range === 'string' ? { date: range } : range || {} }),
  getFaireStatus: () => api.get('/v1/wholesale/faire/status'),
  getSevenShiftsStatus: () => api.get('/v1/integrations/7shifts/status'),
  startSevenShiftsOauth: () => api.get('/v1/integrations/7shifts/oauth/start'),
  getSquareStatus: () => api.get('/v1/integrations/square/status'),
  getDeliveryStatus: (platform: string) => api.get(`/v1/integrations/delivery/${platform}/status`),
  getSalesSummary: () => api.get('/v1/integrations/sales-summary'),
  startUbereatsOauth: () => api.get('/v1/integrations/delivery/ubereats/oauth/start'),
  connectDoordash: (businessId?: string) => api.post('/v1/integrations/delivery/doordash/connect', businessId ? { businessId } : {}),
  syncDelivery: (platform: string) => api.post(`/v1/integrations/delivery/${platform}/sync`),
  disconnectDelivery: (platform: string) => api.delete(`/v1/integrations/delivery/${platform}/connect`),
  startSquareOauth: () => api.get('/v1/integrations/square/oauth/start'),
  disconnectSquare: () => api.delete('/v1/integrations/square/connect'),
  syncSquare: () => api.post('/v1/integrations/square/sync'),
  importSquareMenu: () => api.post('/v1/integrations/square/import-menu'),
  getQuickbooksStatus: () => api.get('/v1/integrations/quickbooks/status'),
  startQuickbooksOauth: () => api.get('/v1/integrations/quickbooks/oauth/start'),
  disconnectQuickbooks: () => api.delete('/v1/integrations/quickbooks/connect'),
  syncQuickbooks: () => api.post('/v1/integrations/quickbooks/sync'),
  getGmailStatus: () => api.get('/v1/integrations/gmail/status'),
  startGmailOauth: () => api.get('/v1/integrations/gmail/oauth/start'),
  disconnectGmail: () => api.delete('/v1/integrations/gmail/connect'),
  syncGmail: () => api.post('/v1/integrations/gmail/sync'),
  getInstagramStatus: () => api.get('/v1/integrations/instagram/status'),
  getActions: () => api.get('/v1/actions'),
  resolveAction: (id: string, status: 'done' | 'dismissed') => api.patch(`/v1/actions/${id}`, { status }),
  approveInvoiceAction: (id: string, priceCents: number, email: string) => api.post(`/v1/actions/${id}/approve-invoice`, { priceCents, email }),
  connectSevenShifts: (accessToken: string) => api.post('/v1/integrations/7shifts/connect', { accessToken }),
  disconnectSevenShifts: () => api.delete('/v1/integrations/7shifts/connect'),
  syncSevenShifts: () => api.post('/v1/integrations/7shifts/sync'),
  connectFaire: (applicationToken: string, applicationSecret: string, accessToken?: string) => api.post('/v1/wholesale/faire/connect', { applicationToken, applicationSecret, ...(accessToken ? { accessToken } : {}) }),
  disconnectFaire: () => api.delete('/v1/wholesale/faire/connect'),
  syncFaire: () => api.post('/v1/wholesale/faire/sync'),
  getInvoices: (params?: { status?: string; entity?: string }) => api.get('/v1/wholesale/invoices', { params }),
  createInvoice: (orderId: string) => api.post(`/v1/wholesale/orders/${orderId}/invoice`),
  sendInvoice: (id: string, email?: string) => api.post(`/v1/wholesale/invoices/${id}/send`, email ? { email } : {}),
  sendOrderInvoice: (orderId: string, body: { invoiceNumber?: string; dueAt?: string; to?: string; bodyText?: string; markOnly?: boolean }) =>
    api.post(`/v1/wholesale/orders/${orderId}/send-invoice`, body),
  uploadOrderAttachment: (orderId: string, file: File, kind: 'po' | 'bol' | 'other') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/v1/wholesale/orders/${orderId}/attachments?kind=${kind}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadOrderAttachment: (orderId: string, fileId: string) => api.get(`/v1/wholesale/orders/${orderId}/attachments/${fileId}`, { responseType: 'blob' }),
  deleteOrderAttachment: (orderId: string, fileId: string) => api.delete(`/v1/wholesale/orders/${orderId}/attachments/${fileId}`),
  setInvoiceStatus: (id: string, status: 'paid' | 'unpaid') => api.patch(`/v1/wholesale/invoices/${id}`, { status }),
  recordPayment: (id: string, amountCents: number, deduction?: { amountCents: number; reasonCode: string; notes?: string }, extra?: { receivedAt?: string; paymentMethod?: string }) =>
    api.post(`/v1/wholesale/invoices/${id}/payment`, {
      amountCents,
      ...(deduction ? { deduction } : {}),
      ...(extra?.receivedAt ? { receivedAt: extra.receivedAt } : {}),
      ...(extra?.paymentMethod ? { paymentMethod: extra.paymentMethod } : {}),
    }),
  // Deduction reconciliation
  getDeductions: (params?: { customerId?: string; entity?: string }) => api.get('/v1/wholesale/deductions', { params }),
  getDeductionSummary: (entity?: string) => api.get('/v1/wholesale/deductions/summary', { params: entity ? { entity } : {} }),
  createDeduction: (data: Record<string, unknown>) => api.post('/v1/wholesale/deductions', data),
  updateDeduction: (id: string, data: Record<string, unknown>) => api.patch(`/v1/wholesale/deductions/${id}`, data),
  deleteDeduction: (id: string) => api.delete(`/v1/wholesale/deductions/${id}`),
};

// CPG Hub — imports & lots, landed cost, organic mass balance, accounts pipeline
export const hubApi = {
  getImports: (entity?: string) => api.get('/v1/hub/imports', { params: entity ? { entity } : {} }),
  getImport: (id: string) => api.get(`/v1/hub/imports/${id}`),
  createImport: (data: Record<string, unknown>) => api.post('/v1/hub/imports', data),
  updateImport: (id: string, data: Record<string, unknown>) => api.patch(`/v1/hub/imports/${id}`, data),
  deleteImport: (id: string) => api.delete(`/v1/hub/imports/${id}`),
  // Hapag-Lloyd container tracking: live ETA with a platform key, deep link without
  trackImport: (id: string) => api.post(`/v1/hub/imports/${id}/track`, {}),
  addImportLine: (importId: string, data: Record<string, unknown>) => api.post(`/v1/hub/imports/${importId}/lines`, data),
  updateImportLine: (importId: string, lineId: string, data: Record<string, unknown>) => api.patch(`/v1/hub/imports/${importId}/lines/${lineId}`, data),
  deleteImportLine: (importId: string, lineId: string) => api.delete(`/v1/hub/imports/${importId}/lines/${lineId}`),
  addLot: (importId: string, data: Record<string, unknown>) => api.post(`/v1/hub/imports/${importId}/lots`, data),
  updateLot: (importId: string, lotId: string, data: Record<string, unknown>) => api.patch(`/v1/hub/imports/${importId}/lots/${lotId}`, data),
  deleteLot: (importId: string, lotId: string) => api.delete(`/v1/hub/imports/${importId}/lots/${lotId}`),
  getExpiringLots: (days = 60) => api.get('/v1/hub/lots/expiring', { params: { days } }),
  getCostTrend: () => api.get('/v1/hub/cost-trend'),
  getProducts: () => api.get('/v1/hub/products'),
  setProductOrganic: (menuItemId: string, organic: boolean) => api.patch(`/v1/hub/products/${menuItemId}/organic`, { organic }),
  getMassBalance: () => api.get('/v1/hub/mass-balance'),
  getPipeline: () => api.get('/v1/hub/pipeline'),
  setCustomerStage: (id: string, stage: string) => api.patch(`/v1/hub/customers/${id}/stage`, { stage }),
  addCustomerActivity: (id: string, data: { note: string; nextStep?: string; dueAt?: string }) =>
    api.post(`/v1/hub/customers/${id}/activities`, data),
  deleteCustomerActivity: (id: string, activityId: string) =>
    api.delete(`/v1/hub/customers/${id}/activities/${activityId}`),
};

export const recipesApi = {
  getAll: () => api.get('/v1/recipes', { params: { includeIngredients: true } }),
  getMargins: () => api.get('/v1/recipes/margins'),
  getById: (id: string) => api.get(`/v1/recipes/${id}`),
  create: (data: CreateRecipeData) => api.post('/v1/recipes', data),
  update: (id: string, data: Partial<CreateRecipeData>) =>
    api.patch(`/v1/recipes/${id}`, data),
  delete: (id: string) => api.delete(`/v1/recipes/${id}`),
  addIngredient: (recipeId: string, data: RecipeIngredientData) =>
    api.post(`/v1/recipes/${recipeId}/ingredients`, data),
  updateIngredient: (recipeId: string, ingredientId: string, data: Partial<RecipeIngredientData>) =>
    api.patch(`/v1/recipes/${recipeId}/ingredients/${ingredientId}`, data),
  removeIngredient: (recipeId: string, ingredientId: string) =>
    api.delete(`/v1/recipes/${recipeId}/ingredients/${ingredientId}`),
  getCost: (id: string) => api.get(`/v1/recipes/${id}/cost`),
  linkMenuItem: (recipeId: string, menuItemId: string) =>
    api.post(`/v1/recipes/${recipeId}/link-menu-item`, { menuItemId }),
};

interface CreateRecipeData {
  laborMinutes?: number;
  name: string;
  description?: string;
  yield?: number;
  yieldUnit?: string;
  prepTimeMinutes?: number;
  cookTimeMinutes?: number;
  instructions?: string;
  menuItemId?: string;
}

interface RecipeIngredientData {
  subRecipeId?: string;
  inventoryItemId: string;
  quantity: number;
  unit: string;
  wasteFactor?: number;
  isOptional?: boolean;
  notes?: string;
}

// Checks endpoints
export const checksApi = {
  getOpen: () => api.get('/v1/checks/open'),
  getById: (id: string) => api.get(`/v1/checks/${id}`),
};

// Restaurant endpoints
export const restaurantApi = {
  getCurrent: () => api.get('/v1/restaurants/current'),
  // Source files — the documents the account was set up from
  getFiles: () => api.get('/v1/restaurants/current/files'),
  uploadFile: (file: File) => { const fd = new FormData(); fd.append('file', file); return api.post('/v1/restaurants/current/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  downloadFile: (fileId: string) => api.get(`/v1/restaurants/current/files/${fileId}/download`, { responseType: 'blob' }),
  deleteFile: (fileId: string) => api.delete(`/v1/restaurants/current/files/${fileId}`),
  updateSettings: (settings: Record<string, unknown>) =>
    api.patch('/v1/restaurants/current/settings', settings),
  getUnitPreferences: () => api.get('/v1/restaurants/current/unit-preferences'),
  updateUnitPreferences: (preferences: { weight?: string; volume?: string; count?: string }) =>
    api.patch('/v1/restaurants/current/unit-preferences', preferences),
};

// Payroll endpoints
export const payrollApi = {
  // Pay periods
  getPayPeriods: () => api.get('/v1/payroll/pay-periods'),
  createPayPeriod: (data: CreatePayPeriodData) =>
    api.post('/v1/payroll/pay-periods', data),
  getPayPeriod: (id: string) => api.get(`/v1/payroll/pay-periods/${id}`),

  // Payroll runs
  createPayrollRun: (periodId: string) =>
    api.post(`/v1/payroll/pay-periods/${periodId}/runs`),
  getPayrollRun: (runId: string) => api.get(`/v1/payroll/runs/${runId}`),
  approvePayrollRun: (runId: string) =>
    api.post(`/v1/payroll/runs/${runId}/approve`),
  processPayrollRun: (runId: string) =>
    api.post(`/v1/payroll/runs/${runId}/process`),

  // Bank accounts
  createLinkToken: (employeeId: string) =>
    api.post('/v1/payroll/bank-accounts/link-token', { employeeId }),
  connectBankAccount: (data: ConnectBankAccountData) =>
    api.post('/v1/payroll/bank-accounts', data),
  getEmployeeBankAccounts: (employeeId: string) =>
    api.get(`/v1/payroll/employees/${employeeId}/bank-accounts`),
  deleteBankAccount: (accountId: string) =>
    api.delete(`/v1/payroll/bank-accounts/${accountId}`),

  // Stripe Connect
  createStripeOnboarding: (employeeId: string, urls: { refreshUrl: string; returnUrl: string }) =>
    api.post(`/v1/payroll/employees/${employeeId}/stripe-onboarding`, urls),
  getStripeStatus: (employeeId: string) =>
    api.get(`/v1/payroll/employees/${employeeId}/stripe-status`),
};

interface CreatePayPeriodData {
  startDate: string;
  endDate: string;
  payDate: string;
  notes?: string;
}

interface ConnectBankAccountData {
  employeeId: string;
  publicToken: string;
  accountId: string;
}

// Guests endpoints
export const guestsApi = {
  getAll: (params?: { search?: string; isVip?: boolean; limit?: number; offset?: number }) =>
    api.get('/v1/guests', { params }),
  search: (q: string) => api.get('/v1/guests/search', { params: { q } }),
  getById: (id: string) => api.get(`/v1/guests/${id}`),
  create: (data: CreateGuestData) => api.post('/v1/guests', data),
  update: (id: string, data: Partial<CreateGuestData>) =>
    api.patch(`/v1/guests/${id}`, data),
  addNote: (id: string, note: string) =>
    api.post(`/v1/guests/${id}/notes`, { note }),
  markVip: (id: string, isVIP: boolean, notes?: string) =>
    api.post(`/v1/guests/${id}/vip`, { isVIP, notes }),
  getInsights: (id: string) =>
    api.get(`/v1/guests/${id}/insights`),
  // Tag management
  getTags: (id: string) =>
    api.get(`/v1/guests/${id}/tags`),
  addTag: (id: string, data: { tag: string; layer: string; notes?: string }) =>
    api.post(`/v1/guests/${id}/tags`, data),
  removeTag: (guestId: string, tagId: string) =>
    api.delete(`/v1/guests/${guestId}/tags/${tagId}`),
  computeTags: (id: string) =>
    api.post(`/v1/guests/${id}/tags/compute`),
  getTagSuggestions: () =>
    api.get('/v1/guests/tags/suggestions'),
  // Tag Definitions (Glossary)
  getTagDefinitions: (params?: { layer?: string; includeSystem?: boolean }) =>
    api.get('/v1/guests/tags/definitions', { params }),
  createTagDefinition: (data: { name: string; layer: string; description?: string; color?: string }) =>
    api.post('/v1/guests/tags/definitions', data),
  updateTagDefinition: (id: string, data: { name?: string; description?: string; color?: string; isActive?: boolean }) =>
    api.put(`/v1/guests/tags/definitions/${id}`, data),
  deleteTagDefinition: (id: string) =>
    api.delete(`/v1/guests/tags/definitions/${id}`),
  refreshAllTags: () =>
    api.post('/v1/guests/tags/refresh-all'),
  // Celebrations
  getUpcomingCelebrations: (params?: { days?: number; type?: 'birthday' | 'anniversary' | 'all' }) =>
    api.get('/v1/guests/celebrations/upcoming', { params }),
  sendCelebrationReminder: (data: { guestId: string; occasion: 'birthday' | 'anniversary'; customMessage?: string }) =>
    api.post('/v1/guests/celebrations/send-reminder', data),
  resetCelebrationReminder: (data: { guestId: string; occasion: 'birthday' | 'anniversary'; celebrationDate: string }) =>
    api.delete('/v1/guests/celebrations/reset-reminder', { data }),
  // Update special dates
  updateSpecialDates: (id: string, data: { birthday?: string; anniversary?: string }) =>
    api.patch(`/v1/guests/${id}`, data),
};

interface CreateGuestData {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  notes?: string;
  dietaryRestrictions?: string[];
  allergies?: string[];
  preferences?: Record<string, unknown>;
  isVIP?: boolean;
  birthday?: string;
  anniversary?: string;
}

// Plaid endpoints
export const plaidApi = {
  createLinkToken: (employeeId: string) =>
    api.post('/v1/plaid/link-token', { employeeId }),
  exchangeToken: (data: { employeeId: string; publicToken: string; accountId: string }) =>
    api.post('/v1/plaid/exchange-token', data),
  getAccounts: (employeeId: string) =>
    api.get(`/v1/plaid/accounts/${employeeId}`),
  removeAccount: (bankAccountId: string) =>
    api.delete(`/v1/plaid/accounts/${bankAccountId}`),
  getPayoutStatus: (employeeId: string) =>
    api.get(`/v1/plaid/payout-status/${employeeId}`),
  // Send bank connection invitation to employee
  sendInvite: (employeeId: string, email?: string) =>
    api.post(`/v1/payroll/employees/${employeeId}/send-bank-invite`, email ? { email } : {}),
};

// Public API (no auth required) - for employee self-service flows
const API_URL = import.meta.env.VITE_API_URL || DEFAULT_API_URL;

export const publicApi = {
  // Validate bank connection invitation token
  validateBankInvite: async (token: string) => {
    const response = await fetch(`${API_URL}/v1/plaid/invite/${token}`);
    return response.json();
  },

  // Get Plaid link token via invitation
  getBankLinkToken: async (token: string) => {
    const response = await fetch(`${API_URL}/v1/plaid/invite/${token}/link-token`, {
      method: 'POST',
    });
    return response.json();
  },

  // Exchange Plaid public token via invitation
  exchangeBankToken: async (
    token: string,
    data: { publicToken: string; accountId: string }
  ) => {
    const response = await fetch(`${API_URL}/v1/plaid/invite/${token}/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// Pre-Shift Config endpoints
export const preshiftConfigApi = {
  // Daily Specials
  getSpecials: (date?: string) =>
    api.get('/v1/preshift-config/specials', { params: { date } }),
  addSpecial: (data: AddSpecialData) =>
    api.post('/v1/preshift-config/specials', data),
  removeSpecial: (specialId: string) =>
    api.delete(`/v1/preshift-config/specials/${specialId}`),

  // Announcements
  getAnnouncements: (params?: { role?: string; includeExpired?: boolean }) =>
    api.get('/v1/preshift-config/announcements', { params }),
  createAnnouncement: (data: CreateAnnouncementData) =>
    api.post('/v1/preshift-config/announcements', data),
  updateAnnouncement: (id: string, data: Partial<CreateAnnouncementData>) =>
    api.patch(`/v1/preshift-config/announcements/${id}`, data),
  deleteAnnouncement: (id: string) =>
    api.delete(`/v1/preshift-config/announcements/${id}`),

  // 86'd Items
  getEightySixItems: () =>
    api.get('/v1/preshift-config/eighty-six'),
  toggleEightySix: (menuItemId: string, data: { isAvailable: boolean; availableQuantity?: number }) =>
    api.post(`/v1/preshift-config/eighty-six/${menuItemId}`, data),

  // Preview
  getPreview: (role: string) =>
    api.get(`/v1/preshift-config/preview/${role}`),

  // Menu Search
  searchMenuItems: (query?: string, categoryId?: string) =>
    api.get('/v1/preshift-config/menu-search', { params: { query, categoryId } }),

  // Menu Item Staff Details
  updateMenuItemDetails: (menuItemId: string, data: {
    staffDescription?: string;
    allergenInfo?: string;
    ingredientsList?: string;
    pairingSuggestions?: string[];
    prepNotes?: string;
  }) => api.patch(`/v1/menu/items/${menuItemId}`, data),
};

interface AddSpecialData {
  menuItemId: string;
  date: string;
  specialPriceCents?: number;
  description?: string;
  sortOrder?: number;
}

interface CreateAnnouncementData {
  title: string;
  message: string;
  priority?: 'urgent' | 'high' | 'normal' | 'low';
  targetRoles?: string[];
  startsAt?: string;
  expiresAt?: string;
  isActive?: boolean;
}

// ===========================================
// SHIFT TEMPLATES API
// ===========================================
export const shiftTemplatesApi = {
  getAll: (params?: { isActive?: boolean }) =>
    api.get('/v1/shift-templates', { params }),
  getById: (id: string) =>
    api.get(`/v1/shift-templates/${id}`),
  create: (data: CreateShiftTemplateData) =>
    api.post('/v1/shift-templates', data),
  update: (id: string, data: Partial<CreateShiftTemplateData>) =>
    api.patch(`/v1/shift-templates/${id}`, data),
  delete: (id: string) =>
    api.delete(`/v1/shift-templates/${id}`),
};

export interface RoleRequirement {
  role: string;
  minCount: number;
  maxCount?: number;
}

export interface CreateShiftTemplateData {
  name: string;
  description?: string;
  color?: string;
  startTime: string;  // HH:mm format
  endTime: string;    // HH:mm format
  roleRequirements?: RoleRequirement[];
  repeatDays?: number[];  // 0=Sunday, 1=Monday, ..., 6=Saturday
  activeSectionIds?: string[];
  isActive?: boolean;
}

export interface ShiftTemplate {
  id: string;
  restaurantId: string;
  name: string;
  description?: string;
  color?: string;
  startTime: string;
  endTime: string;
  roleRequirements: RoleRequirement[];
  repeatDays: number[];
  activeSectionIds: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ===========================================
// SHIFT INSTANCES API
// ===========================================
export const shiftInstancesApi = {
  // Calendar view
  getByDateRange: (startDate: string, endDate: string, status?: string) =>
    api.get('/v1/shift-instances', { params: { startDate, endDate, status } }),
  getById: (id: string) =>
    api.get(`/v1/shift-instances/${id}`),

  // Create instances
  create: (data: CreateShiftInstanceData) =>
    api.post('/v1/shift-instances', data),
  generateFromTemplate: (data: GenerateFromTemplateData) =>
    api.post('/v1/shift-instances/generate', data),
  applyForecast: (startDate: string, endDate: string) =>
    api.post('/v1/shift-instances/apply-forecast', { startDate, endDate }),

  // Update/Delete
  update: (id: string, data: Partial<UpdateShiftInstanceData>) =>
    api.patch(`/v1/shift-instances/${id}`, data),
  delete: (id: string) =>
    api.delete(`/v1/shift-instances/${id}`),

  // Assignments
  getAssignments: (instanceId: string) =>
    api.get(`/v1/shift-instances/${instanceId}/assignments`),
  addAssignment: (instanceId: string, data: AddAssignmentData) =>
    api.post(`/v1/shift-instances/${instanceId}/assignments`, data),
  updateAssignment: (instanceId: string, assignmentId: string, data: Partial<UpdateAssignmentData>) =>
    api.patch(`/v1/shift-instances/${instanceId}/assignments/${assignmentId}`, data),
  removeAssignment: (instanceId: string, assignmentId: string) =>
    api.delete(`/v1/shift-instances/${instanceId}/assignments/${assignmentId}`),

  // Available employees
  getAvailableEmployees: (instanceId: string, role?: string) =>
    api.get(`/v1/shift-instances/${instanceId}/available-employees`, { params: { role } }),

  // Cleanup
  cleanupDuplicates: () =>
    api.post('/v1/shift-instances/cleanup-duplicates'),

  // Publish all draft shifts in a week and notify staff (in-app + email + SMS)
  publishWeek: (startDate: string, endDate: string) =>
    api.post('/v1/shift-instances/publish', { startDate, endDate }),

  // Certification coverage check
  getCertificationCheck: (instanceId: string) =>
    api.get(`/v1/shift-instances/${instanceId}/certification-check`),
};

export interface CreateShiftInstanceData {
  templateId?: string;
  date: string;         // YYYY-MM-DD
  startTime: string;    // ISO datetime
  endTime: string;      // ISO datetime
  name: string;
  color?: string;
  activeSectionIds?: string[];
  roleRequirements?: RoleRequirement[];
  notes?: string;
  // Ad hoc / outdoor event support
  isEvent?: boolean;
  eventType?: string;
  locationLabel?: string;
  expectedGuests?: number;
}

export interface GenerateFromTemplateData {
  templateId: string;
  startDate: string;    // YYYY-MM-DD
  endDate: string;      // YYYY-MM-DD
  skipExisting?: boolean;
}

export interface UpdateShiftInstanceData {
  startTime?: string;
  endTime?: string;
  name?: string;
  color?: string;
  activeSectionIds?: string[];
  roleRequirements?: RoleRequirement[];
  status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
  isEvent?: boolean;
  eventType?: string;
  locationLabel?: string;
  expectedGuests?: number;
}

export interface AddAssignmentData {
  employeeId: string;
  role: string;
  station?: string;
  sectionId?: string;
  tableIds?: string[];
  scheduledStart?: string;
  scheduledEnd?: string;
  notes?: string;
  // Bypass time-off / availability guardrails after manager confirmation
  override?: boolean;
}

export interface UpdateAssignmentData {
  role?: string;
  station?: string;
  sectionId?: string | null;
  tableIds?: string[];
  status?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  notes?: string;
}

export interface ShiftInstance {
  id: string;
  restaurantId: string;
  templateId?: string;
  date: string;
  startTime: string;
  endTime: string;
  name: string;
  color?: string;
  activeSectionIds: string[];
  roleRequirements: RoleRequirement[];
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
  isEvent?: boolean;
  eventType?: string;
  locationLabel?: string;
  expectedGuests?: number;
  published?: boolean;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  // Computed fields from API
  assignments?: ShiftAssignment[];
  staffingSummary?: Array<{
    role: string;
    required: number;
    assigned: number;
    isFilled: boolean;
  }>;
  totalRequired?: number;
  totalAssigned?: number;
  isFilled?: boolean;
}

export interface ShiftAssignment {
  id: string;
  shiftInstanceId: string;
  employeeId: string;
  restaurantId: string;
  role: string;
  station?: string;
  sectionId?: string;
  tableIds?: string[];
  status: string;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart?: string;
  actualEnd?: string;
  scheduledHours: string;
  actualHours?: string;
  breaks: any[];
  totalBreakMinutes?: number;
  salesTotalCents?: number;
  tipsTotalCents?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Populated by API
  employee?: {
    id: string;
    firstName: string;
    lastName: string;
    primaryRole: string;
  };
  section?: {
    id: string;
    name: string;
    color?: string;
  };
}

// ===========================================
// EVENTS (business layer on event shift instances)
// ===========================================

export interface EventMenuSelection {
  menuItemId?: string;
  name: string;
  unitPriceCents: number;
  qtyMode: 'per_guest' | 'flat';
  qty: number;
  notes?: string;
}

export interface EventFinancialsData {
  roomFeeCents?: number;
  servicePct?: number;
  taxPct?: number;
  depositCents?: number;
}

export interface CreateEventInquiryData {
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  clientCompany?: string;
  name?: string;
  eventType?: string;
  date: string;        // YYYY-MM-DD
  startTime?: string;  // HH:mm
  endTime?: string;    // HH:mm
  expectedGuests?: number;
  spaceName?: string;
  notes?: string;
}

export interface UpdateEventDetailsData {
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  clientCompany?: string;
  eventStatus?: 'inquiry' | 'proposal_sent' | 'confirmed' | 'completed' | 'cancelled';
  spaceName?: string;
  menuSelections?: EventMenuSelection[];
  eventFinancials?: EventFinancialsData;
  expectedGuests?: number;
  name?: string;
  notes?: string;
  briefingNotes?: string;
  eventType?: string;
}

export const eventsApi = {
  list: (startDate?: string, endDate?: string) =>
    api.get('/v1/events', { params: { startDate, endDate } }),
  getById: (id: string) =>
    api.get(`/v1/events/${id}`),
  createInquiry: (data: CreateEventInquiryData) =>
    api.post('/v1/events/inquiry', data),
  updateDetails: (id: string, data: UpdateEventDetailsData) =>
    api.patch(`/v1/events/${id}`, data),
  sendProposal: (id: string, data?: { to?: string; message?: string }) =>
    api.post(`/v1/events/${id}/send-proposal`, data || {}),
  createDepositLink: (id: string) =>
    api.post(`/v1/events/${id}/deposit-link`, {}),
  markDepositPaid: (id: string) =>
    api.post(`/v1/events/${id}/deposit/mark-paid`, {}),
  planKitchen: (id: string) =>
    api.post(`/v1/events/${id}/plan-kitchen`, {}),
};

export interface ShiftCertificationWarning {
  type: 'missing_food_protection_supervisor' | 'missing_alcohol_certified_server';
  message: string;
}

export interface ShiftCertificationCoverage {
  shiftInstanceId: string;
  hasCoverage: boolean;
  warnings: ShiftCertificationWarning[];
  certifiedStaffOnShift: Array<{
    employeeId: string;
    name: string;
    certifications: string[];
  }>;
}

// ===========================================
// LABOR MANAGEMENT ENDPOINTS
// ===========================================

// Time-Off Management
export const timeOffApi = {
  // Get all time-off requests for the restaurant
  getAll: (params?: { status?: string; employeeId?: string; startDate?: string; endDate?: string }) =>
    api.get('/v1/time-off', { params }),

  // Get a specific request
  getById: (id: string) => api.get(`/v1/time-off/${id}`),

  // Approve a time-off request
  approve: (id: string, notes?: string) =>
    api.put(`/v1/time-off/${id}/review`, { decision: 'approve', notes }),

  // Deny a time-off request
  deny: (id: string, notes?: string) =>
    api.put(`/v1/time-off/${id}/review`, { decision: 'deny', notes }),

  // Get pending count for badges
  getPendingCount: () =>
    api.get('/v1/time-off', { params: { status: 'pending' } }),
};

// Availability Management
export const availabilityApi = {
  // Get all availability windows for the restaurant
  getAll: (params?: { employeeId?: string; dayOfWeek?: number; status?: string }) =>
    api.get('/v1/availability', { params }),

  // Get availability for a specific employee
  getByEmployee: (employeeId: string) =>
    api.get(`/v1/availability/${employeeId}`),

  // Approve an availability window
  approve: (windowId: string) =>
    api.put(`/v1/availability/${windowId}/review`, { decision: 'approve' }),

  // Reject an availability window
  reject: (windowId: string) =>
    api.put(`/v1/availability/${windowId}/review`, { decision: 'reject' }),

  // Bulk approve/reject
  bulkReview: (windowIds: string[], decision: 'approve' | 'reject') =>
    api.post('/v1/availability/bulk-review', { windowIds, decision }),

  // Check who's available for a specific time
  checkAvailable: (date: string, startTime: string, endTime: string, role?: string) =>
    api.get('/v1/availability/check', { params: { date, startTime, endTime, role } }),
};

// Coverage Management
export const coverageApi = {
  // Get open shifts needing coverage
  getOpenShifts: (params?: { startDate?: string; endDate?: string; role?: string }) =>
    api.get('/v1/coverage/open', { params }),

  // Get coverage gaps (understaffed shifts)
  getGaps: (startDate?: string, endDate?: string) =>
    api.get('/v1/coverage/gaps', { params: { startDate, endDate } }),

  // Get ranked substitutes for an assignment
  getSubstitutes: (assignmentId: string, limit?: number) =>
    api.get(`/v1/coverage/substitutes/${assignmentId}`, { params: { limit } }),

  // Get candidates to fill a gap (new position, not replacement)
  getGapCandidates: (shiftInstanceId: string, role: string, limit?: number) =>
    api.get(`/v1/coverage/gaps/${shiftInstanceId}/candidates`, { params: { role, limit } }),

  // Fill a gap (create new assignment)
  fillGap: (shiftInstanceId: string, employeeId: string, role: string, notes?: string) =>
    api.post('/v1/coverage/gaps/fill', { shiftInstanceId, employeeId, role, notes }),

  // Request coverage (send to employees)
  requestCoverage: (assignmentId: string, targetEmployeeIds?: string[], message?: string) =>
    api.post('/v1/coverage/request', { assignmentId, targetEmployeeIds, message }),

  // Assign coverage (manager directly assigns)
  assignCoverage: (assignmentId: string, employeeId: string, notes?: string) =>
    api.post('/v1/coverage/assign', { assignmentId, employeeId, notes }),
};

// Shift Swap Management
export const shiftSwapsApi = {
  // Get all swap requests
  getAll: (params?: { status?: string; employeeId?: string; type?: string }) =>
    api.get('/v1/shift-swaps', { params }),

  // Get pending requests for manager review
  getPending: () => api.get('/v1/shift-swaps/pending'),

  // Approve a swap request
  approve: (requestId: string, notes?: string) =>
    api.put(`/v1/shift-swaps/${requestId}/review`, { decision: 'approve', notes }),

  // Deny a swap request
  deny: (requestId: string, notes?: string) =>
    api.put(`/v1/shift-swaps/${requestId}/review`, { decision: 'deny', notes }),
};

// Labor Forecasting & Recommendations
export const laborApi = {
  // Get staffing forecast for a specific date
  getForecast: (date: string, refresh?: boolean) =>
    api.get(`/v1/labor/forecast/${date}`, { params: { refresh } }),

  // Get forecast for a date range
  getForecastRange: (startDate: string, endDate: string, refresh?: boolean) =>
    api.get('/v1/labor/forecast', { params: { startDate, endDate, refresh } }),

  // Get labor cost projection
  getCostProjection: (startDate: string, endDate: string) =>
    api.get('/v1/labor/cost-projection', { params: { startDate, endDate } }),

  // Get optimization suggestions
  getOptimizationSuggestions: (startDate: string, endDate: string) =>
    api.get('/v1/labor/optimization', { params: { startDate, endDate } }),

  // Compare scheduled vs recommended staffing
  getComparison: (date: string) =>
    api.get(`/v1/labor/compare/${date}`),

  // Get weekly labor summary
  getWeeklySummary: (weekOf?: string) =>
    api.get('/v1/labor/weekly-summary', { params: { weekOf } }),
};

// Labor Forecast Types
export interface LaborForecast {
  id: string;
  forecastDate: string;
  expectedCovers: number;
  expectedRevenueCents: number;
  recommendedFohCount: number;
  recommendedBohCount: number;
  recommendedByRole: Record<string, number>;
  confidenceScore: number;
  factors?: {
    reservationCovers: number;
    walkInEstimate: number;
    historicalAverage: number;
    dayOfWeekFactor: number;
  };
}

export interface StaffingComparison {
  date: string;
  forecast: {
    expectedCovers: number;
    confidenceScore: number;
  };
  foh: { scheduled: number; recommended: number; diff: number };
  boh: { scheduled: number; recommended: number; diff: number };
  byRole: Record<string, { scheduled: number; recommended: number; diff: number }>;
  status: 'adequate' | 'understaffed' | 'overstaffed';
}

export interface OptimizationSuggestion {
  type: 'overstaffed' | 'understaffed' | 'overtime_risk' | 'cost_saving';
  date: string;
  message: string;
  impact: string;
  suggestion: string;
}

// ===========================================
// HOST API
// ===========================================
export const hostApi = {
  // Get aggregated dashboard data (tables, reservations, waitlist, stats)
  getOverview: () => api.get('/v1/host/overview'),

  // Seat a walk-in party
  seatWalkIn: (data: SeatWalkInData) => api.post('/v1/host/seat-walk-in', data),

  // Check table availability for party size and time
  getAvailability: (params: { partySize: number; time?: string; date?: string }) =>
    api.get('/v1/host/availability', { params }),

  // Block/unblock a table
  blockTable: (tableId: string, data: { blocked: boolean; reason?: string }) =>
    api.patch(`/v1/host/block-table/${tableId}`, data),

  // Get cover projections by hour
  getCoverForecast: () => api.get('/v1/host/cover-forecast'),

  // Check in a reservation (guest arrived but not yet seated)
  checkInReservation: (reservationId: string) =>
    api.post(`/v1/host/check-in/${reservationId}`),

  // Get floor status by section
  getFloorStatus: (sectionId?: string) =>
    api.get('/v1/host/floor-status', { params: sectionId ? { sectionId } : {} }),

  // Get AI-powered occupancy optimization suggestions
  getOptimizationSuggestions: (date?: string) =>
    api.get('/v1/host/optimization-suggestions', { params: date ? { date } : {} }),
};

interface SeatWalkInData {
  tableId: string;
  partySize: number;
  guestName?: string;
  guestPhone?: string;
  guestId?: string;
}

// ===========================================
// WAITLIST API
// ===========================================
export const waitlistApi = {
  // Get current waitlist
  getAll: () => api.get('/v1/waitlist'),

  // Add party to waitlist
  add: (data: AddToWaitlistData) => api.post('/v1/waitlist', data),

  // Update waitlist entry (status, quoted time, etc.)
  update: (id: string, data: Partial<AddToWaitlistData>) =>
    api.patch(`/v1/waitlist/${id}`, data),

  // Send "table ready" notification
  sendText: (id: string) => api.post(`/v1/waitlist/${id}/text`),

  // Seat party from waitlist
  seat: (id: string, tableId: string) =>
    api.post(`/v1/waitlist/${id}/seat`, { tableId }),

  // Remove from waitlist
  remove: (id: string) => api.delete(`/v1/waitlist/${id}`),

  // Get waitlist statistics
  getStats: () => api.get('/v1/waitlist/stats'),
};

interface AddToWaitlistData {
  guestName: string;
  partySize: number;
  guestPhone?: string;
  guestId?: string;
  quotedWaitMinutes?: number;
  preferredSectionId?: string;
  specialRequests?: string;
}

// Waitlist entry type
export interface WaitlistEntry {
  id: string;
  restaurantId: string;
  guestId?: string;
  guestName: string;
  guestPhone?: string;
  partySize: number;
  quotedWaitMinutes?: number;
  status: 'waiting' | 'texted' | 'arrived' | 'seated' | 'left' | 'no_show';
  position: number;
  preferredSectionId?: string;
  specialRequests?: string;
  addedAt: string;
  textedAt?: string;
  arrivedAt?: string;
  seatedAt?: string;
  seatedTableId?: string;
  checkId?: string;
  notes?: string;
  waitTimeMinutes?: number;
}

// ===========================================
// HIRE INVITES & COMPLIANCE API
// ===========================================

export const hireInvitesApi = {
  // List all hire invites
  getAll: (params?: { status?: string }) =>
    api.get('/v1/hire-invites', { params }),

  // Get a specific hire invite
  getById: (id: string) => api.get(`/v1/hire-invites/${id}`),

  // Create a new hire invite
  create: (data: CreateHireInviteData) =>
    api.post('/v1/hire-invites', data),

  // Update a hire invite
  update: (id: string, data: Partial<CreateHireInviteData>) =>
    api.patch(`/v1/hire-invites/${id}`, data),

  // Send the invite email
  send: (id: string) =>
    api.post(`/v1/hire-invites/${id}/send`),

  // Resend the invite email
  resend: (id: string) =>
    api.post(`/v1/hire-invites/${id}/resend`),

  // Record conditional offer (enables background check)
  recordConditionalOffer: (id: string) =>
    api.post(`/v1/hire-invites/${id}/conditional-offer`),

  // Order background check
  orderBackgroundCheck: (id: string, data?: { vendor?: string }) =>
    api.post(`/v1/hire-invites/${id}/background-check/order`, data),

  // Delete/cancel a hire invite
  delete: (id: string) => api.delete(`/v1/hire-invites/${id}`),
};

export interface CreateHireInviteData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  primaryRole: string;
  locationId?: string;
  wageType?: 'hourly' | 'salary';
  proposedHourlyRateCents?: number;
  proposedStartDate?: string;
  pin?: string;
  // Compliance flags
  requiresBackgroundCheck?: boolean;
  alcoholServiceInvolved?: boolean;
  foodHandlingInvolved?: boolean;
  drivingInvolved?: boolean;
  supervisionInvolved?: boolean;
}

export interface HireInvite {
  id: string;
  restaurantId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  primaryRole: string;
  status: 'pending' | 'sent' | 'in_progress' | 'conditional_offer' | 'background_check' | 'completed' | 'cancelled';
  inviteToken: string;
  wageType?: string;
  proposedWageCents?: number;
  proposedStartDate?: string;
  requiresBackgroundCheck?: boolean;
  alcoholServiceInvolved?: boolean;
  foodHandlingInvolved?: boolean;
  sentAt?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Compliance API
export const complianceApi = {
  // Dashboard stats
  getDashboard: () => api.get('/v1/compliance/dashboard'),
  getDashboardStats: () => api.get('/v1/compliance/dashboard-stats'),

  // Get items expiring soon
  getExpiring: (days?: number) =>
    api.get('/v1/compliance/expiring', { params: { days } }),

  // Get incomplete onboarding
  getIncomplete: () => api.get('/v1/compliance/incomplete'),

  // Get employee compliance status
  getEmployeeStatus: (employeeId: string) =>
    api.get(`/v1/staff/${employeeId}/compliance`),

  // Verify/approve a compliance item
  verifyItem: (itemId: string, data: VerifyComplianceItemData) =>
    api.post(`/v1/compliance/items/${itemId}/verify`, data),

  // Reject a compliance item
  rejectItem: (itemId: string, data: { notes?: string }) =>
    api.post(`/v1/compliance/items/${itemId}/reject`, data),

  // Get restaurant onboarding info
  getOnboardingInfo: () => api.get('/v1/restaurant/onboarding-info'),

  // Update restaurant onboarding info
  updateOnboardingInfo: (data: UpdateOnboardingInfoData) =>
    api.put('/v1/restaurant/onboarding-info', data),

  // State rules
  getStateRules: (stateCode: string, cityCode?: string) =>
    api.get(`/v1/compliance/state-rules/${stateCode}`, { params: cityCode ? { cityCode } : {} }),
  getAllStateRules: () => api.get('/v1/compliance/all-state-rules'),

  // Work authorization
  getExpiringWorkAuth: (daysAhead: number = 90) =>
    api.get('/v1/compliance/expiring-work-auth', { params: { daysAhead } }),

  // Certifications
  getExpiringCertifications: (daysAhead: number = 30, certTypes?: string[]) =>
    api.get('/v1/compliance/expiring-certifications', { params: { daysAhead, certTypes } }),
  renewCertification: (id: string, data: {
    newExpirationDate: string;
    documentId?: string;
    certificationNumber?: string;
    issuingAuthority?: string;
    issuedAt?: string;
    verificationNotes?: string;
  }) => api.post(`/v1/compliance/certifications/${id}/renew`, data),
};

export interface VerifyComplianceItemData {
  documentNumber?: string;
  expirationDate?: string;
  verificationNotes?: string;
}

export interface UpdateOnboardingInfoData {
  welcomeMessage?: string;
  dressCode?: string;
  uniformRequirements?: string;
  groomingStandards?: string;
  serviceStandards?: string;
  firstDayInstructions?: string;
  parkingInfo?: string;
  menuUrl?: string;
  trainingMaterialsUrl?: string;
  employeeHandbookUrl?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  additionalNotes?: Record<string, unknown>;
}

export interface ComplianceItem {
  id: string;
  checklistId: string;
  itemType: string;
  status: 'not_started' | 'in_progress' | 'pending_review' | 'approved' | 'rejected' | 'expired';
  dueDate?: string;
  expiresAt?: string;
  verifiedBy?: string;
  verifiedAt?: string;
  formData?: Record<string, unknown>;
}

export interface ComplianceChecklist {
  id: string;
  employeeId?: string;
  hireInviteId?: string;
  status: 'incomplete' | 'complete' | 'blocked';
  requiredItems: string[];
  completedItems: string[];
  items: ComplianceItem[];
}

export interface ComplianceDashboardStats {
  pendingReview: number;
  expiringThisWeek: number;
  expiringThisMonth: number;
  incompleteOnboarding: number;
  totalCompliant: number;
  totalNonCompliant: number;
}

// ===========================================
// DOCUMENTS API
// ===========================================
export const documentsApi = {
  // Upload document for an employee
  upload: (employeeId: string, file: File, documentType: string, documentSubtype?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('employeeId', employeeId);
    formData.append('documentType', documentType);
    if (documentSubtype) {
      formData.append('documentSubtype', documentSubtype);
    }
    return api.post('/v1/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Get all documents for an employee
  getByEmployee: (employeeId: string) =>
    api.get(`/v1/documents/employee/${employeeId}`),

  // Get a single document
  getById: (documentId: string) =>
    api.get(`/v1/documents/${documentId}`),

  // Verify a document
  verify: (documentId: string, data?: {
    verificationNotes?: string;
    expirationDate?: string;
    documentNumber?: string;
    issuingAuthority?: string;
  }) => api.post(`/v1/documents/${documentId}/verify`, data || {}),

  // Delete a document
  delete: (documentId: string) =>
    api.delete(`/v1/documents/${documentId}`),
};

export interface ComplianceDocument {
  id: string;
  documentType: string;
  documentSubtype?: string;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  isVerified: boolean;
  verifiedAt?: string;
  verifiedBy?: string;
  verificationMethod?: string;
  verificationNotes?: string;
  documentNumber?: string;
  issuingAuthority?: string;
  issueDate?: string;
  expirationDate?: string;
  createdAt: string;
}

export interface ComplianceAlert {
  type: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  count: number;
}

export interface ComplianceStats {
  pendingOnboarding: number;
  awaitingReview: number;
  overdueNewHireReports: number;
  workAuthExpiringWithin30Days: number;
  certificationsExpiringWithin30Days: number;
  alerts: ComplianceAlert[];
}

// ===========================================
// TRAININGS API
// ===========================================
export const trainingsApi = {
  // Training Types
  getTypes: (params?: { category?: string; activeOnly?: boolean }) =>
    api.get('/v1/trainings/types', { params }),
  createType: (data: CreateTrainingTypeData) =>
    api.post('/v1/trainings/types', data),
  updateType: (id: string, data: Partial<CreateTrainingTypeData>) =>
    api.patch(`/v1/trainings/types/${id}`, data),
  seedDefaultTypes: () =>
    api.post('/v1/trainings/types/seed'),

  // Employee Trainings
  getEmployeeTrainings: (employeeId: string) =>
    api.get(`/v1/trainings/employee/${employeeId}`),
  assignTraining: (data: AssignTrainingData) =>
    api.post('/v1/trainings/assign', data),
  assignForRole: (data: AssignTrainingsForRoleData) =>
    api.post('/v1/trainings/assign-for-role', data),
  startTraining: (id: string) =>
    api.post(`/v1/trainings/${id}/start`),
  completeTraining: (id: string, data: CompleteTrainingData) =>
    api.post(`/v1/trainings/${id}/complete`, data),
  deleteTraining: (id: string) =>
    api.delete(`/v1/trainings/${id}`),

  // Monitoring
  getExpiring: (daysAhead: number = 30) =>
    api.get('/v1/trainings/expiring', { params: { daysAhead } }),
  getIncomplete: () =>
    api.get('/v1/trainings/incomplete'),
  getStats: () =>
    api.get('/v1/trainings/stats'),
};

export interface CreateTrainingTypeData {
  name: string;
  description?: string;
  category: 'onboarding' | 'safety' | 'service' | 'technical' | 'compliance';
  isRequired?: boolean;
  requiredForRoles?: string[];
  validityMonths?: number;
  estimatedMinutes?: number;
}

export interface AssignTrainingData {
  employeeId: string;
  trainingTypeId: string;
  dueDate?: string;
  notes?: string;
}

export interface AssignTrainingsForRoleData {
  employeeId: string;
  role: string;
  dueDate?: string;
}

export interface CompleteTrainingData {
  verificationMethod: 'manager_sign_off' | 'quiz_passed' | 'certificate_uploaded' | 'self_reported';
  score?: number;
  notes?: string;
}

export interface TrainingType {
  id: string;
  restaurantId: string | null;
  name: string;
  description?: string;
  category: string;
  isRequired: boolean;
  requiredForRoles: string[];
  validityMonths?: number;
  estimatedMinutes?: number;
  trainingUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeTraining {
  id: string;
  restaurantId: string;
  employeeId: string;
  trainingTypeId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'expired';
  assignedAt: string;
  dueDate?: string;
  completedAt?: string;
  expiresAt?: string;
  verifiedBy?: string;
  verifiedAt?: string;
  verificationMethod?: string;
  score?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  trainingType?: TrainingType;
}

export interface TrainingStats {
  totalTrainingTypes: number;
  totalAssignedTrainings: number;
  completedTrainings: number;
  pendingTrainings: number;
  overdueTrainings: number;
  expiringWithin30Days: number;
  completionRate: number;
}

// Billing & Subscription endpoints
export const billingApi = {
  getSubscription: () => api.get('/v1/billing'),
  getAccess: () => api.get('/v1/billing/access'),
  getPricing: () => api.get('/v1/billing/pricing'),
  createCheckout: (data: { tier: string; interval: string; successUrl: string; cancelUrl: string }) =>
    api.post('/v1/billing/checkout', data),
  changeTier: (data: { tier: string; interval: string }) =>
    api.post('/v1/billing/change-tier', data),
  cancel: () => api.post('/v1/billing/cancel'),
  reactivate: () => api.post('/v1/billing/reactivate'),
  createPortalSession: (returnUrl: string) =>
    api.post('/v1/billing/portal', { returnUrl }),
  getPaymentMethods: () => api.get('/v1/billing/payment-methods'),
  createSetupIntent: () => api.post('/v1/billing/setup-intent'),
  getUpcomingInvoice: () => api.get('/v1/billing/upcoming-invoice'),
  getInvoices: () => api.get('/v1/billing/invoices'),
  // Stripe Connect — the restaurant's own payout account
  connectOnboard: () => api.post('/v1/stripe-connect/onboard'),
  connectStatus: () => api.get('/v1/stripe-connect/status'),
};

// End-of-day close-out
export const eodApi = {
  summary: (date: string, tzOffsetMinutes: number) =>
    api.get('/v1/eod/summary', { params: { date, tzOffsetMinutes } }),
  close: (data: { date: string; tzOffsetMinutes: number; cashCountedCents: number; notes?: string }) =>
    api.post('/v1/eod/close', data),
  list: () => api.get('/v1/eod'),
};

// Guest payments taken at the POS (list + refunds)
export const guestPaymentsApi = {
  list: (limit = 50) => api.get('/v1/payments', { params: { limit } }),
  refund: (paymentId: string, data: { amount: number; reason: string; managerPin?: string }) =>
    api.post(`/v1/payments/${paymentId}/refund`, data),
};

// Managers' daily logbook
export const logbookApi = {
  day: (date: string) => api.get('/v1/logbook', { params: { date, tzOffsetMinutes: new Date().getTimezoneOffset() } }),
  add: (data: { date: string; body: string; category?: string; spanDays?: number }) => api.post('/v1/logbook', data),
  remove: (id: string) => api.delete(`/v1/logbook/${id}`),
  update: (id: string, data: { body?: string; category?: string; spanDays?: number }) => api.patch(`/v1/logbook/${id}`, data),
  search: (q: string) => api.get('/v1/logbook/search', { params: { q } }),
  analysis: (start: string, end: string) => api.get('/v1/logbook/analysis', { params: { start, end, tzOffsetMinutes: new Date().getTimezoneOffset() } }),
  transcribe: (audio: Blob) => {
    const fd = new FormData();
    fd.append('file', audio, 'note.webm');
    return api.post('/v1/logbook/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Staff messages — one thread per employee with the manager group
export const staffMessagesApi = {
  threads: () => api.get('/v1/staff-messages/threads'),
  unread: () => api.get('/v1/staff-messages/unread-count'),
  thread: (employeeId: string) => api.get(`/v1/staff-messages/thread/${employeeId}`),
  send: (employeeId: string, body: string) => api.post(`/v1/staff-messages/thread/${employeeId}`, { body }),
  pin: (messageId: string, pinned: boolean) => api.post(`/v1/staff-messages/message/${messageId}/pin`, { pinned }),
};

export default api;
